import { useEffect, useState } from "react";
import { ArrowsClockwise } from "@phosphor-icons/react";

/**
 * LivePill — small status pill showing live-refresh state + last-update timestamp.
 *
 * Pair with `useLivePoll` (or any setInterval) — pass it the timestamp of the
 * latest successful refresh and an onRefresh handler for the manual button.
 */
export function LivePill({ updatedAt, onRefresh, busy = false, label = "Live", testid = "live-pill" }) {
  const [, force] = useState(0);
  // Re-render every 10s so the "Updated Ns ago" text stays fresh between polls
  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 10000);
    return () => clearInterval(id);
  }, []);

  const ago = updatedAt ? secondsAgo(updatedAt) : null;

  return (
    <div className="inline-flex items-center gap-2 text-[11px]" data-testid={testid}>
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-nk-gold/10 border border-nk-gold/40 text-nk-gold uppercase tracking-[0.16em]">
        <span className="w-1.5 h-1.5 rounded-full bg-nk-gold animate-pulse-dot" />
        {label}
      </span>
      {ago !== null && (
        <span className="text-white/45 font-mono">Updated {formatAgo(ago)}</span>
      )}
      {onRefresh && (
        <button
          type="button"
          onClick={onRefresh}
          disabled={busy}
          data-testid={`${testid}-refresh`}
          className="ml-1 text-white/55 hover:text-nk-gold disabled:opacity-40 transition-colors"
          title="Refresh now"
        >
          <ArrowsClockwise size={12} weight="bold" className={busy ? "animate-spin" : ""} />
        </button>
      )}
    </div>
  );
}

function secondsAgo(ts) {
  return Math.max(0, Math.floor((Date.now() - ts) / 1000));
}

function formatAgo(s) {
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  return `${m}m ago`;
}
